// Dev Priority Page - Priority list with expandable rows and drag-and-drop

import React, { useState, useEffect } from 'react';
import {
  Spinner,
  MessageBar,
  MessageBarType,
  useTheme,
  IconButton,
} from '@fluentui/react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import type { DropResult } from '@hello-pangea/dnd';
import { useDevPriority } from '../DevPriorityContext';
import EditableCell from '../components/EditableCell';

const DevPriorityPage: React.FC = () => {
  const theme = useTheme();
  const { tickets, userType, loading, error, fetchTickets, updateTicketPriority, reorderTickets } = useDevPriority();
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const toggleRowExpansion = (ticketId: number) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(ticketId)) {
        newSet.delete(ticketId);
      } else {
        newSet.add(ticketId);
      }
      return newSet;
    });
  };

  const updateItem = async (ticketId: number, fieldName: string, value: string | number) => {
    if (fieldName === 'perceivedPriority') {
      const newValue = parseInt(value.toString());
      if (!isNaN(newValue)) {
        try {
          await updateTicketPriority(ticketId, newValue);
        } catch (err) {
          console.error('Failed to update priority:', err);
        }
      }
    }
  };

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination || userType !== 'Developer') return;

    const sourceIndex = result.source.index;
    const destIndex = result.destination.index;

    if (sourceIndex === destIndex) return;

    const draggedTicket = tickets[sourceIndex];
    
    try {
      await reorderTickets(draggedTicket.ticketId, destIndex);
    } catch (err) {
      console.error('Failed to reorder:', err);
    }
  };

  const columns = [
    { key: 'ticketId', name: 'Ticket #', width: '80px' },
    { key: 'order', name: 'Order', width: '60px' },
    { key: 'owner', name: 'Owner', width: '150px' },
    { key: 'perceivedPriority', name: 'Priority', width: '80px' },
    { key: 'title', name: 'Title', width: 'auto' },
    { key: 'status', name: 'Status', width: '150px' },
    { key: 'budgetHours', name: 'Budget Hours', width: '100px' },
    { key: 'expand', name: '', width: '50px' },
  ];

  if (loading && tickets.length === 0) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <Spinner label="Loading priorities..." />
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      <h1 style={{ color: theme.palette.neutralPrimary, marginBottom: '10px' }}>
        Dev Priority List
      </h1>
      <p style={{ color: theme.palette.neutralSecondary, marginBottom: '20px' }}>
        To set priority for a ticket, click on the priority field and enter a value. Use higher
        numbers for higher priority and lower numbers for lower priority.
        {userType === 'Developer' && ' Drag and drop rows to reorder.'}
      </p>

      {error && (
        <MessageBar
          messageBarType={MessageBarType.error}
          onDismiss={() => {}}
          dismissButtonAriaLabel="Close"
          style={{ marginBottom: '20px' }}
        >
          {error}
        </MessageBar>
      )}

      {tickets.length > 0 ? (
        <div style={{ overflow: 'auto' }}>
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              backgroundColor: theme.palette.white,
              boxShadow: theme.effects.elevation4,
            }}
          >
            <thead>
              <tr
                style={{
                  backgroundColor: theme.palette.neutralLighter,
                  borderBottom: `2px solid ${theme.palette.neutralLight}`,
                }}
              >
                {columns.map((col) => (
                  <th
                    key={col.key}
                    style={{
                      padding: '12px 8px',
                      textAlign: 'left',
                      fontWeight: 600,
                      color: theme.palette.neutralPrimary,
                      width: col.width,
                    }}
                  >
                    {col.name}
                  </th>
                ))}
              </tr>
            </thead>
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="priority-list">
                {(provided, snapshot) => (
                  <tbody
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    style={{
                      backgroundColor: snapshot.isDraggingOver
                        ? theme.palette.neutralLighterAlt
                        : 'transparent',
                    }}
                  >
                    {tickets.map((ticket, index) => {
                      const isExpanded = expandedRows.has(ticket.ticketId);
                      return (
                        <React.Fragment key={ticket.ticketId}>
                          <Draggable
                            draggableId={String(ticket.ticketId)}
                            index={index}
                            isDragDisabled={userType !== 'Developer'}
                          >
                            {(provided, snapshot) => (
                              <tr
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                style={{
                                  ...provided.draggableProps.style,
                                  userSelect: 'none',
                                  backgroundColor: snapshot.isDragging
                                    ? theme.palette.neutralLight
                                    : theme.palette.white,
                                  borderBottom: `1px solid ${theme.palette.neutralLight}`,
                                }}
                              >
                                <td style={{ padding: '8px', color: theme.palette.neutralPrimary }}>
                                  {ticket.ticketId}
                                </td>
                                <td style={{ padding: '8px', color: theme.palette.neutralPrimary }}>
                                  {ticket.order}
                                </td>
                                <td style={{ padding: '8px', color: theme.palette.neutralPrimary }}>
                                  {ticket.owner}
                                </td>
                                <td style={{ padding: '8px' }}>
                                  <EditableCell
                                    item={ticket}
                                    column={{ fieldName: 'perceivedPriority' } as any}
                                    updateItem={updateItem}
                                  />
                                </td>
                                <td
                                  style={{
                                    padding: '8px',
                                    color: theme.palette.themePrimary,
                                    cursor: 'pointer',
                                    textDecoration: 'underline',
                                  }}
                                  onClick={() => toggleRowExpansion(ticket.ticketId)}
                                >
                                  {ticket.title}
                                </td>
                                <td style={{ padding: '8px', color: theme.palette.neutralPrimary }}>
                                  {ticket.status}
                                </td>
                                <td style={{ padding: '8px', color: theme.palette.neutralPrimary }}>
                                  {ticket.budgetHours}
                                </td>
                                <td style={{ padding: '8px', textAlign: 'center' }}>
                                  <IconButton
                                    iconProps={{
                                      iconName: isExpanded ? 'ChevronDown' : 'ChevronRight',
                                    }}
                                    onClick={() => toggleRowExpansion(ticket.ticketId)}
                                    title={isExpanded ? 'Collapse' : 'Expand'}
                                  />
                                </td>
                              </tr>
                            )}
                          </Draggable>
                          {isExpanded && (
                            <tr>
                              <td
                                colSpan={columns.length}
                                style={{
                                  padding: '16px',
                                  backgroundColor: theme.palette.neutralLighterAlt,
                                  borderLeft: `3px solid ${theme.palette.themePrimary}`,
                                  borderBottom: `1px solid ${theme.palette.neutralLight}`,
                                }}
                              >
                                <div style={{ color: theme.palette.neutralPrimary }}>
                                  <strong>Description:</strong>
                                </div>
                                <div
                                  style={{
                                    marginTop: '8px',
                                    whiteSpace: 'pre-wrap',
                                    color: theme.palette.neutralPrimary,
                                  }}
                                >
                                  {ticket.description || 'No description available'}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                    {provided.placeholder}
                  </tbody>
                )}
              </Droppable>
            </DragDropContext>
          </table>
        </div>
      ) : (
        !loading && (
          <div
            style={{
              textAlign: 'center',
              padding: '40px',
              color: theme.palette.neutralSecondary,
            }}
          >
            No tickets available.
          </div>
        )
      )}
    </div>
  );
};

export default DevPriorityPage;
